import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOrders, type SavedOrder } from "@/hooks/useOrders";
import { supabase } from "@/lib/supabase";
import OrdersList from "@/pages/account/components/OrdersList";
import RewardsCard from "@/pages/account/components/RewardsCard";
import PendingReviewsBanner from "@/pages/account/components/PendingReviewsBanner";
import { useFavorites } from "@/hooks/useFavorites";

type Tab = "profile" | "orders" | "rewards";

export default function AccountPage() {
  const { user, logout, isLoggedIn, login, updateCustomerName, updateCustomerEmail, updateCustomerAddress, updateCustomerPhone } = useAuth();
  const { getOrders } = useOrders();
  const { totalItems: favCount } = useFavorites();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("profile");
  const [orders, setOrders] = useState<SavedOrder[]>([]);
  const [editingName, setEditingName] = useState(false);
  const linkedRef = useRef(false);
  const [newName, setNewName] = useState("");
  const [nameError, setNameError] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState("تم الحفظ بنجاح");

  // Email editing
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  // Address editing
  const [editingAddress, setEditingAddress] = useState(false);
  const [newCity, setNewCity] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [savingAddress, setSavingAddress] = useState(false);

  // Phone editing (for email-only users)
  const [editingPhone, setEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);

  // Password change
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordChangeError, setPasswordChangeError] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) navigate("/auth");
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (!user) return;

    // Pass customerId so email-only users can see their orders too
    getOrders(user.phone, user.id).then(setOrders);

    // One-time: link any remaining unlinked orders by phone to this customer_id
    if (!linkedRef.current && user.id && user.phone) {
      linkedRef.current = true;
      supabase
        .from("orders")
        .update({ customer_id: user.id })
        .eq("phone", user.phone)
        .is("customer_id", null)
        .then(() => {
          // silently done
        });
    }
  }, [user, getOrders]);

  const handleSaveName = useCallback(async () => {
    if (!newName.trim() || newName.trim().length < 3) {
      setNameError("الاسم يجب أن يكون 3 أحرف على الأقل");
      return;
    }
    if (!user) return;
    setSavingName(true);
    setNameError("");
    try {
      const ok = await updateCustomerName(user.phone, newName.trim());
      if (ok) {
        login({ ...user, name: newName.trim() });
        setEditingName(false);
        setSaveSuccessMsg("تم حفظ الاسم بنجاح");
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setNameError("حدث خطأ أثناء الحفظ، حاول مرة أخرى");
      }
    } finally {
      setSavingName(false);
    }
  }, [newName, user, login, updateCustomerName]);

  const handleSavePhone = useCallback(async () => {
    if (!newPhone.trim()) { setPhoneError("يرجى إدخال رقم الجوال"); return; }
    if (!user) return;
    setSavingPhone(true);
    setPhoneError("");
    try {
      const { error } = await updateCustomerPhone(user.id, newPhone.trim());
      if (!error) {
        setEditingPhone(false);
        setNewPhone("");
        setSaveSuccessMsg("تم حفظ رقم الجوال بنجاح — ستصلك إشعارات واتساب الآن");
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 4000);
      } else {
        setPhoneError(error);
      }
    } finally {
      setSavingPhone(false);
    }
  }, [newPhone, user, updateCustomerPhone]);

  const handleSaveAddress = useCallback(async () => {
    if (!newCity.trim()) { setAddressError("يرجى إدخال المدينة"); return; }
    if (!newAddress.trim()) { setAddressError("يرجى إدخال العنوان التفصيلي"); return; }
    if (!user) return;
    setSavingAddress(true);
    setAddressError("");
    try {
      const identifier = user.phone || user.id;
      const ok = await updateCustomerAddress(identifier, newCity.trim(), newAddress.trim());
      if (ok) {
        // Update local auth state immediately so it reflects everywhere
        login({ ...user, city: newCity.trim(), address: newAddress.trim() });
        setEditingAddress(false);
        setSaveSuccessMsg("تم حفظ العنوان بنجاح");
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setAddressError("حدث خطأ أثناء الحفظ، حاول مرة أخرى");
      }
    } finally {
      setSavingAddress(false);
    }
  }, [newCity, newAddress, user, updateCustomerAddress, login]);

  const handleSaveEmail = useCallback(async () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) { setEmailError("يرجى إدخال البريد الإلكتروني"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError("يرجى إدخال بريد إلكتروني صحيح");
      return;
    }
    if (!user) return;
    setSavingEmail(true);
    setEmailError("");
    try {
      const { error } = await updateCustomerEmail(user.id, trimmed);
      if (!error) {
        setEditingEmail(false);
        setSaveSuccessMsg("تم حفظ البريد الإلكتروني بنجاح");
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setEmailError(error);
      }
    } finally {
      setSavingEmail(false);
    }
  }, [newEmail, user, updateCustomerEmail]);

  const handleChangePassword = async () => {
    if (!currentPassword) { setPasswordChangeError("يرجى إدخال كلمة المرور الحالية"); return; }
    if (!newPassword || newPassword.length < 6) { setPasswordChangeError("كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل"); return; }
    if (newPassword !== confirmNewPassword) { setPasswordChangeError("كلمتا المرور الجديدة غير متطابقتين"); return; }
    if (!user?.id) return;

    setSavingPassword(true);
    setPasswordChangeError("");

    try {
      // Hash current password and verify
      const encoder = new TextEncoder();
      const hashBuffer = async (pwd: string) => {
        const buf = await crypto.subtle.digest("SHA-256", encoder.encode(pwd));
        return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
      };

      const currentHash = await hashBuffer(currentPassword);

      const { data: customer } = await supabase
        .from("customers")
        .select("password_hash")
        .eq("id", user.id)
        .maybeSingle();

      if (!customer) { setPasswordChangeError("حدث خطأ، حاول مرة أخرى"); return; }

      // If account has no password yet, allow setting one directly
      if (customer.password_hash && customer.password_hash !== currentHash) {
        setPasswordChangeError("كلمة المرور الحالية غير صحيحة");
        return;
      }

      const newHash = await hashBuffer(newPassword);
      const { error } = await supabase
        .from("customers")
        .update({ password_hash: newHash })
        .eq("id", user.id);

      if (error) { setPasswordChangeError("حدث خطأ أثناء الحفظ، حاول مرة أخرى"); return; }

      setChangingPassword(false);
      setCurrentPassword(""); setNewPassword(""); setConfirmNewPassword("");
      setSaveSuccessMsg("تم تغيير كلمة المرور بنجاح");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } finally {
      setSavingPassword(false);
    }
  };

  if (!user) return null;

  const joinDate = new Date(user.joinedAt).toLocaleDateString("ar-SA", {
    year: "numeric", month: "long", day: "numeric",
  });

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const totalSpent = orders.reduce((sum, o) => sum + o.finalPrice, 0);
  const pendingOrders = orders.filter((o) => o.status === "pending").length;
  const deliveredOrders = orders.filter((o) => o.status === "delivered").length;

  return (
    <div className="min-h-screen bg-[#0a0a0a] px-4 py-16" dir="rtl">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-amber-400/4 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 left-1/4 w-[300px] h-[300px] bg-amber-400/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-3xl mx-auto">
        {/* Back */}
        <a
          href="/"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer mb-8"
          style={{ fontFamily: "'Cairo', sans-serif" }}
        >
          <i className="ri-arrow-right-line"></i>
          العودة للمتجر
        </a>

        {/* Save success toast */}
        {saveSuccess && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-green-500/90 text-white text-sm font-bold px-5 py-3 rounded-2xl flex items-center gap-2 animate-bounce" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-checkbox-circle-line text-base"></i>
            {saveSuccessMsg}
          </div>
        )}

        {/* WhatsApp nudge banner — email-only users without phone */}
        {!user.phone && !editingPhone && (
          <div
            className="flex items-center gap-4 bg-green-500/8 border border-green-500/20 rounded-2xl px-5 py-4 mb-4 cursor-pointer hover:bg-green-500/12 transition-all"
            onClick={() => { setTab("profile"); setEditingPhone(true); }}
          >
            <div className="w-10 h-10 flex items-center justify-center bg-green-500/15 rounded-xl flex-shrink-0">
              <i className="ri-whatsapp-line text-green-400 text-xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-green-400 text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                فعّل إشعارات واتساب لطلباتك
              </p>
              <p className="text-green-400/60 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                أضف رقم جوالك لتصلك تحديثات الطلبات فوراً عبر واتساب
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-1.5 bg-green-500/15 border border-green-500/25 rounded-full px-3 py-1.5">
              <i className="ri-add-line text-green-400 text-xs"></i>
              <span className="text-green-400 text-xs font-bold whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif" }}>إضافة الجوال</span>
            </div>
          </div>
        )}

        {/* Profile Hero Card */}
        <div className="bg-gradient-to-br from-[#1a1a1a] to-[#111] border border-white/8 rounded-3xl p-6 mb-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-400/0 via-amber-400/60 to-amber-400/0"></div>

          {/* Cloud saved badge */}
          <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-green-400/10 border border-green-400/20 rounded-full px-3 py-1">
            <i className="ri-cloud-line text-green-400 text-xs"></i>
            <span className="text-green-400 text-[10px] font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>محفوظ في السحابة</span>
          </div>

          <div className="flex items-center gap-5 mt-4">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-amber-400/30 to-amber-400/10 rounded-2xl border border-amber-400/20">
                <span className="text-amber-400 text-3xl font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {user.name.charAt(0)}
                </span>
              </div>
              <div className="absolute -bottom-1 -left-1 w-6 h-6 flex items-center justify-center bg-green-400 rounded-full border-2 border-[#111]">
                <i className="ri-check-line text-black text-xs font-black"></i>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-white text-xl font-black truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {user.name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <i className={`${user.phone ? "ri-smartphone-line" : "ri-mail-line"} text-gray-500 text-xs`}></i>
                <span className="text-gray-400 text-sm tracking-wider truncate" style={{ fontFamily: "'Cairo', sans-serif", direction: user.phone ? "ltr" : "ltr" }}>
                  {user.phone || user.email || "—"}
                </span>
              </div>
              <p className="text-gray-600 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-calendar-line ml-1"></i>
                عضو منذ {joinDate}
              </p>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="flex-shrink-0 flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 text-red-400 font-bold px-4 py-2.5 rounded-xl text-xs transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-logout-box-r-line text-sm"></i>
              خروج
            </button>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="bg-[#0f0f0f] rounded-2xl px-4 py-3 text-center border border-white/5">
              <p className="text-amber-400 text-xl font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {orders.length}
              </p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>إجمالي الطلبات</p>
            </div>
            <div className="bg-[#0f0f0f] rounded-2xl px-4 py-3 text-center border border-white/5">
              <p className="text-green-400 text-xl font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {deliveredOrders}
              </p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>تم التوصيل</p>
            </div>
            <div className="bg-[#0f0f0f] rounded-2xl px-4 py-3 text-center border border-white/5">
              <p className="text-white text-lg font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {totalSpent > 0 ? `${totalSpent.toLocaleString()}` : "—"}
              </p>
              <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {totalSpent > 0 ? "ر.س مجموع الإنفاق" : "لا توجد طلبات"}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#111] border border-white/8 rounded-2xl p-1 mb-5">
          <button
            onClick={() => setTab("profile")}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              tab === "profile" ? "bg-amber-400 text-black" : "text-gray-400 hover:text-white"
            }`}
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-user-3-line text-base"></i>
            حسابي
          </button>
          <button
            onClick={() => setTab("orders")}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              tab === "orders" ? "bg-amber-400 text-black" : "text-gray-400 hover:text-white"
            }`}
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-shopping-bag-3-line text-base"></i>
            طلباتي
            {orders.length > 0 && (
              <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-black ${
                tab === "orders" ? "bg-black/20 text-black" : "bg-amber-400/20 text-amber-400"
              }`}>
                {orders.length}
              </span>
            )}
            {pendingOrders > 0 && tab !== "orders" && (
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
            )}
          </button>
          <button
            onClick={() => setTab("rewards")}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
              tab === "rewards" ? "bg-amber-400 text-black" : "text-gray-400 hover:text-white"
            }`}
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <i className="ri-vip-crown-line text-base"></i>
            مكافآتي
          </button>
        </div>

        {/* ── Profile Tab ── */}
        {tab === "profile" && (
          <div className="flex flex-col gap-4">
            {/* Account Info */}
            <div className="bg-[#111] border border-white/8 rounded-3xl p-5">
              <h3 className="text-white font-black text-sm mb-4 flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <i className="ri-user-settings-line text-amber-400"></i>
                معلومات الحساب
              </h3>
              <div className="flex flex-col gap-3">
                {/* Name field with edit */}
                <div className="flex items-center gap-4 bg-[#1a1a1a] rounded-2xl px-5 py-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl flex-shrink-0">
                    <i className="ri-user-3-line text-amber-400 text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>الاسم الكامل</p>
                    {editingName ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="text"
                          value={newName}
                          onChange={(e) => { setNewName(e.target.value); setNameError(""); }}
                          onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                          className="flex-1 bg-[#0f0f0f] border border-amber-400/30 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-amber-400/60 min-w-0"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                          autoFocus
                        />
                        <button
                          onClick={handleSaveName}
                          disabled={savingName}
                          className="flex-shrink-0 bg-amber-400 hover:bg-amber-300 text-black text-xs font-black px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          {savingName ? "..." : "حفظ"}
                        </button>
                        <button
                          onClick={() => { setEditingName(false); setNameError(""); }}
                          className="flex-shrink-0 text-gray-500 hover:text-gray-300 text-xs px-2 py-1.5 cursor-pointer whitespace-nowrap"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          إلغاء
                        </button>
                      </div>
                    ) : (
                      <p className="text-white text-sm font-bold mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>{user.name}</p>
                    )}
                    {nameError && <p className="text-red-400 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>{nameError}</p>}
                  </div>
                  {!editingName && (
                    <button
                      onClick={() => { setEditingName(true); setNewName(user.name); }}
                      className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-amber-400/10 rounded-lg transition-all cursor-pointer"
                    >
                      <i className="ri-edit-line text-gray-400 hover:text-amber-400 text-sm"></i>
                    </button>
                  )}
                </div>

                {/* Phone field — show verified badge if has phone, or add form if email-only */}
                {user.phone ? (
                  <div className="flex items-center gap-4 bg-[#1a1a1a] rounded-2xl px-5 py-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-green-400/10 rounded-xl flex-shrink-0">
                      <i className="ri-smartphone-line text-green-400 text-base"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>رقم الجوال</p>
                      <p className="text-white text-sm font-bold mt-0.5 tracking-wider" style={{ fontFamily: "'Cairo', sans-serif" }}>{user.phone}</p>
                    </div>
                    <div className="flex items-center gap-1.5 bg-green-400/10 border border-green-400/20 rounded-full px-3 py-1">
                      <i className="ri-shield-check-line text-green-400 text-xs"></i>
                      <span className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>مُتحقق</span>
                    </div>
                  </div>
                ) : (
                  /* Email-only user — add phone CTA */
                  <div className={`bg-[#1a1a1a] rounded-2xl px-5 py-4 border ${editingPhone ? "border-amber-400/20" : "border-dashed border-white/10"}`}>
                    {editingPhone ? (
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-green-400/10 rounded-xl flex-shrink-0">
                            <i className="ri-whatsapp-line text-green-400 text-base"></i>
                          </div>
                          <div>
                            <p className="text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>إضافة رقم الجوال</p>
                            <p className="text-gray-500 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>لاستقبال إشعارات الطلبات عبر واتساب</p>
                          </div>
                        </div>
                        <div className="relative">
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm select-none">
                            🇸🇦 +966
                          </span>
                          <input
                            type="tel"
                            value={newPhone}
                            onChange={(e) => { setNewPhone(e.target.value); setPhoneError(""); }}
                            onKeyDown={(e) => e.key === "Enter" && handleSavePhone()}
                            placeholder="05XXXXXXXX"
                            maxLength={15}
                            className={`w-full bg-[#0f0f0f] border ${phoneError ? "border-red-500/60" : "border-amber-400/30"} rounded-xl pr-28 pl-4 py-3 text-white text-sm outline-none focus:border-amber-400/60 transition-colors placeholder-gray-600`}
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                            autoFocus
                          />
                        </div>
                        {phoneError && (
                          <p className="text-red-400 text-xs flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            <i className="ri-error-warning-line"></i>{phoneError}
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={handleSavePhone}
                            disabled={savingPhone}
                            className="flex-1 bg-green-500 hover:bg-green-400 disabled:opacity-60 text-white font-black py-2.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                          >
                            {savingPhone ? (
                              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>جاري الحفظ...</>
                            ) : (
                              <><i className="ri-whatsapp-line text-base"></i>حفظ وتفعيل واتساب</>
                            )}
                          </button>
                          <button
                            onClick={() => { setEditingPhone(false); setNewPhone(""); setPhoneError(""); }}
                            className="px-4 py-2.5 text-gray-500 hover:text-gray-300 text-sm rounded-xl border border-white/8 hover:border-white/15 transition-all cursor-pointer whitespace-nowrap"
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-amber-400/8 rounded-xl flex-shrink-0">
                          <i className="ri-smartphone-line text-amber-400/60 text-base"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>رقم الجوال</p>
                          <p className="text-gray-600 text-sm mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>لم يُضف بعد</p>
                          <p className="text-amber-400/70 text-[11px] mt-1 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            <i className="ri-whatsapp-line text-green-400/70"></i>
                            أضف جوالك لاستقبال إشعارات الطلبات عبر واتساب
                          </p>
                        </div>
                        <button
                          onClick={() => { setEditingPhone(true); setNewPhone(""); }}
                          className="flex-shrink-0 flex items-center gap-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 hover:border-green-500/40 text-green-400 font-bold px-3 py-2 rounded-xl text-xs transition-all cursor-pointer whitespace-nowrap"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          <i className="ri-add-line text-sm"></i>
                          إضافة
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Email field */}
                <div className="flex items-center gap-4 bg-[#1a1a1a] rounded-2xl px-5 py-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl flex-shrink-0">
                    <i className="ri-mail-line text-amber-400 text-base"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>البريد الإلكتروني</p>
                    {editingEmail ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => { setNewEmail(e.target.value); setEmailError(""); }}
                          onKeyDown={(e) => e.key === "Enter" && handleSaveEmail()}
                          placeholder="example@email.com"
                          className="flex-1 bg-[#0f0f0f] border border-amber-400/30 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-amber-400/60 min-w-0"
                          style={{ fontFamily: "'Cairo', sans-serif", direction: "ltr" }}
                          autoFocus
                        />
                        <button
                          onClick={handleSaveEmail}
                          disabled={savingEmail}
                          className="flex-shrink-0 bg-amber-400 hover:bg-amber-300 text-black text-xs font-black px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          {savingEmail ? "..." : "حفظ"}
                        </button>
                        <button
                          onClick={() => { setEditingEmail(false); setEmailError(""); }}
                          className="flex-shrink-0 text-gray-500 hover:text-gray-300 text-xs px-2 py-1.5 cursor-pointer whitespace-nowrap"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        >
                          إلغاء
                        </button>
                      </div>
                    ) : (
                      <p className="text-white text-sm font-bold mt-0.5 truncate" style={{ fontFamily: "'Cairo', sans-serif", direction: "ltr" }}>
                        {user.email ?? <span className="text-gray-600 font-normal" style={{ direction: "rtl" }}>لم يُضف بعد</span>}
                      </p>
                    )}
                    {emailError && <p className="text-red-400 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>{emailError}</p>}
                  </div>
                  {!editingEmail && (
                    <button
                      onClick={() => { setEditingEmail(true); setNewEmail(user.email ?? ""); }}
                      className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-amber-400/10 rounded-lg transition-all cursor-pointer"
                      title={user.email ? "تعديل البريد" : "إضافة بريد إلكتروني"}
                    >
                      <i className={`${user.email ? "ri-edit-line" : "ri-add-line"} text-gray-400 hover:text-amber-400 text-sm`}></i>
                    </button>
                  )}
                </div>

                {/* Address field */}
                <div className={`bg-[#1a1a1a] rounded-2xl px-5 py-4 ${editingAddress ? "" : "flex items-center gap-4"}`}>
                  {editingAddress ? (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl flex-shrink-0">
                          <i className="ri-map-pin-2-line text-amber-400 text-base"></i>
                        </div>
                        <p className="text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>تعديل العنوان</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div>
                          <label className="text-gray-500 text-xs block mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>المدينة</label>
                          <input
                            type="text"
                            value={newCity}
                            onChange={(e) => { setNewCity(e.target.value); setAddressError(""); }}
                            placeholder="مثال: الرياض"
                            className="w-full bg-[#0f0f0f] border border-amber-400/30 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-400/60 placeholder-gray-600"
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="text-gray-500 text-xs block mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>العنوان التفصيلي</label>
                          <textarea
                            value={newAddress}
                            onChange={(e) => { setNewAddress(e.target.value); setAddressError(""); }}
                            placeholder="مثال: حي النزهة، شارع الأمير سلطان، مبنى 12"
                            rows={2}
                            className="w-full bg-[#0f0f0f] border border-amber-400/30 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-amber-400/60 placeholder-gray-600 resize-none"
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                          />
                        </div>
                        {addressError && (
                          <p className="text-red-400 text-xs flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            <i className="ri-error-warning-line"></i>{addressError}
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveAddress}
                            disabled={savingAddress}
                            className="flex-1 bg-amber-400 hover:bg-amber-300 text-black text-sm font-black py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                          >
                            <i className="ri-save-line text-base"></i>
                            {savingAddress ? "جاري الحفظ..." : "حفظ العنوان"}
                          </button>
                          <button
                            onClick={() => { setEditingAddress(false); setAddressError(""); }}
                            className="px-4 py-2.5 text-gray-500 hover:text-gray-300 text-sm rounded-xl border border-white/8 hover:border-white/15 transition-all cursor-pointer whitespace-nowrap"
                            style={{ fontFamily: "'Cairo', sans-serif" }}
                          >
                            إلغاء
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl flex-shrink-0">
                        <i className="ri-map-pin-2-line text-amber-400 text-base"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>المدينة والعنوان</p>
                        {user.city || user.address ? (
                          <div className="mt-0.5">
                            {user.city && (
                              <p className="text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                                <i className="ri-map-pin-line text-amber-400/60 ml-1 text-xs"></i>
                                {user.city}
                              </p>
                            )}
                            {user.address && (
                              <p className="text-gray-400 text-xs mt-0.5 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>{user.address}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-600 text-sm mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>لم يُضف بعد</p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setEditingAddress(true);
                          setNewCity(user.city ?? "");
                          setNewAddress(user.address ?? "");
                        }}
                        className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-amber-400/10 rounded-lg transition-all cursor-pointer"
                        title={user.city ? "تعديل العنوان" : "إضافة عنوان"}
                      >
                        <i className={`${user.city ? "ri-edit-line" : "ri-add-line"} text-gray-400 hover:text-amber-400 text-sm`}></i>
                      </button>
                    </>
                  )}
                </div>

                {/* Join date */}
                <div className="flex items-center gap-4 bg-[#1a1a1a] rounded-2xl px-5 py-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl flex-shrink-0">
                    <i className="ri-calendar-check-line text-amber-400 text-base"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>تاريخ التسجيل</p>
                    <p className="text-white text-sm font-bold mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>{joinDate}</p>
                  </div>
                </div>

                {/* Cloud sync info */}
                <div className="flex items-center gap-3 bg-green-400/5 border border-green-400/15 rounded-2xl px-5 py-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-cloud-line text-green-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>حسابك محفوظ في السحابة</p>
                    <p className="text-green-400/60 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>يمكنك تسجيل الدخول من أي جهاز أو متصفح</p>
                  </div>
                </div>

                {/* Orders linked info */}
                <div className="flex items-center gap-3 bg-amber-400/5 border border-amber-400/15 rounded-2xl px-5 py-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-links-line text-amber-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-amber-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>طلباتك مربوطة بحسابك تلقائياً</p>
                    <p className="text-amber-400/60 text-[10px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {orders.length > 0
                        ? `${orders.length} طلب مرتبط بحسابك — حتى الطلبات القديمة`
                        : "أي طلب بنفس رقم جوالك سيظهر هنا تلقائياً"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Password Change Card (email users only) ── */}
            {user.email && (
              <div className="bg-[#111] border border-white/8 rounded-3xl p-5">
                <h3 className="text-white font-black text-sm mb-4 flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-lock-password-line text-amber-400"></i>
                  كلمة المرور
                </h3>

                {!changingPassword ? (
                  <div className="flex items-center gap-4 bg-[#1a1a1a] rounded-2xl px-5 py-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl flex-shrink-0">
                      <i className="ri-lock-2-line text-amber-400 text-base"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>كلمة المرور</p>
                      <p className="text-white text-sm font-bold mt-0.5 tracking-widest">••••••••</p>
                    </div>
                    <button
                      onClick={() => { setChangingPassword(true); setPasswordChangeError(""); }}
                      className="flex items-center gap-2 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/20 hover:border-amber-400/40 text-amber-400 font-bold px-4 py-2 rounded-xl text-xs transition-all cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: "'Cairo', sans-serif" }}
                    >
                      <i className="ri-edit-line text-sm"></i>
                      تغيير
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {/* Current password */}
                    <div>
                      <label className="text-gray-500 text-xs block mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        كلمة المرور الحالية <span className="text-amber-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPwd ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => { setCurrentPassword(e.target.value); setPasswordChangeError(""); }}
                          placeholder="أدخل كلمة مرورك الحالية"
                          className="w-full bg-[#1a1a1a] border border-white/10 focus:border-amber-400/50 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors placeholder-gray-600 pl-11"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer w-5 h-5 flex items-center justify-center"
                        >
                          <i className={showCurrentPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        </button>
                      </div>
                    </div>

                    {/* New password */}
                    <div>
                      <label className="text-gray-500 text-xs block mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        كلمة المرور الجديدة <span className="text-amber-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPwd ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => { setNewPassword(e.target.value); setPasswordChangeError(""); }}
                          placeholder="6 أحرف على الأقل"
                          className="w-full bg-[#1a1a1a] border border-white/10 focus:border-amber-400/50 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors placeholder-gray-600 pl-11"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPwd(!showNewPwd)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer w-5 h-5 flex items-center justify-center"
                        >
                          <i className={showNewPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        </button>
                      </div>
                    </div>

                    {/* Confirm new password */}
                    <div>
                      <label className="text-gray-500 text-xs block mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        تأكيد كلمة المرور الجديدة <span className="text-amber-400">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPwd ? "text" : "password"}
                          value={confirmNewPassword}
                          onChange={(e) => { setConfirmNewPassword(e.target.value); setPasswordChangeError(""); }}
                          onKeyDown={(e) => e.key === "Enter" && handleChangePassword()}
                          placeholder="أعد إدخال كلمة المرور الجديدة"
                          className="w-full bg-[#1a1a1a] border border-white/10 focus:border-amber-400/50 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors placeholder-gray-600 pl-11"
                          style={{ fontFamily: "'Cairo', sans-serif" }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer w-5 h-5 flex items-center justify-center"
                        >
                          <i className={showConfirmPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        </button>
                      </div>
                    </div>

                    {passwordChangeError && (
                      <p className="text-red-400 text-xs flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        <i className="ri-error-warning-line"></i>{passwordChangeError}
                      </p>
                    )}

                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={handleChangePassword}
                        disabled={savingPassword}
                        className="flex-1 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-black py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                        style={{ fontFamily: "'Cairo', sans-serif" }}
                      >
                        {savingPassword ? (
                          <>
                            <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                            جاري الحفظ...
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line text-base"></i>
                            حفظ كلمة المرور
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setChangingPassword(false);
                          setCurrentPassword(""); setNewPassword(""); setConfirmNewPassword("");
                          setPasswordChangeError("");
                        }}
                        className="px-4 py-3 text-gray-500 hover:text-gray-300 text-sm rounded-xl border border-white/8 hover:border-white/15 transition-all cursor-pointer whitespace-nowrap"
                        style={{ fontFamily: "'Cairo', sans-serif" }}
                      >
                        إلغاء
                      </button>
                    </div>

                    <div className="bg-white/3 border border-white/8 rounded-xl px-4 py-3 flex items-start gap-2">
                      <i className="ri-shield-check-line text-amber-400 text-sm mt-0.5 flex-shrink-0"></i>
                      <p className="text-gray-600 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                        كلمة مرورك محفوظة بتشفير آمن — لا يمكن لأحد الاطلاع عليها
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-3 gap-3">
              <a
                href="/"
                className="bg-[#111] border border-white/8 rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-amber-400/30 hover:bg-amber-400/3 transition-all cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl group-hover:bg-amber-400/20 transition-all">
                  <i className="ri-store-2-line text-amber-400 text-lg"></i>
                </div>
                <span className="text-gray-300 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>المتجر</span>
              </a>
              <button
                onClick={() => setTab("orders")}
                className="bg-[#111] border border-white/8 rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-amber-400/30 hover:bg-amber-400/3 transition-all cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl group-hover:bg-amber-400/20 transition-all relative">
                  <i className="ri-file-list-3-line text-amber-400 text-lg"></i>
                  {pendingOrders > 0 && (
                    <span className="absolute -top-1 -left-1 w-4 h-4 flex items-center justify-center bg-amber-400 rounded-full text-black text-[10px] font-black">
                      {pendingOrders}
                    </span>
                  )}
                </div>
                <span className="text-gray-300 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>طلباتي</span>
              </button>
              <a
                href="/favorites"
                className="bg-[#111] border border-white/8 rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-red-400/30 hover:bg-red-400/3 transition-all cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-red-400/10 rounded-xl group-hover:bg-red-400/20 transition-all relative">
                  <i className="ri-heart-line text-red-400 text-lg"></i>
                  {favCount > 0 && (
                    <span className="absolute -top-1 -left-1 w-4 h-4 flex items-center justify-center bg-red-500 rounded-full text-white text-[10px] font-black">
                      {favCount}
                    </span>
                  )}
                </div>
                <span className="text-gray-300 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>المفضلة</span>
              </a>
              <a
                href="/rewards"
                className="bg-[#111] border border-white/8 rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-amber-400/30 hover:bg-amber-400/3 transition-all cursor-pointer group col-span-3 sm:col-span-1"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-amber-400/10 rounded-xl group-hover:bg-amber-400/20 transition-all">
                  <i className="ri-vip-crown-line text-amber-400 text-lg"></i>
                </div>
                <span className="text-gray-300 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>المكافآت</span>
              </a>
            </div>

            {/* Loyalty hint */}
            {orders.length >= 3 && (
              <div className="bg-gradient-to-r from-amber-400/10 to-amber-400/5 border border-amber-400/20 rounded-2xl px-5 py-4 flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-400/20 rounded-xl flex-shrink-0">
                  <i className="ri-medal-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-amber-400 text-sm font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>عميل مميز!</p>
                  <p className="text-amber-400/60 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    لديك {orders.length} طلبات — شكراً لثقتك بنا
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Rewards Tab ── */}
        {tab === "rewards" && (
          <RewardsCard customerId={user.id} />
        )}

        {/* ── Orders Tab ── */}
        {tab === "orders" && (
          <div className="flex flex-col gap-4">
            {/* Pending Reviews Banner */}
            {user.phone && (
              <PendingReviewsBanner orders={orders} userPhone={user.phone} />
            )}

            <div className="bg-[#111] border border-white/8 rounded-3xl p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-white font-black text-base flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-shopping-bag-3-line text-amber-400"></i>
                  طلباتي السابقة
                </h3>
                {orders.length > 0 && (
                  <span className="bg-amber-400/10 border border-amber-400/20 text-amber-400 text-xs font-bold px-3 py-1 rounded-full" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    {orders.length} طلب
                  </span>
                )}
              </div>
              <OrdersList orders={orders} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
